import { api } from './client';
import type { Employee } from './employees';
import type { OrganizationBankAccount, OrganizationKind } from './organizations';

export type PowerOfAttorneyStatus = 'ACTIVE' | 'USED' | 'EXPIRED' | 'REVOKED';

export const POA_STATUS_LABELS: Record<PowerOfAttorneyStatus, string> = {
  ACTIVE: 'Действует',
  USED: 'Отоварена',
  EXPIRED: 'Срок истёк',
  REVOKED: 'Отозвана',
};

export const POA_STATUS_COLORS: Record<PowerOfAttorneyStatus, string> = {
  ACTIVE: 'green',
  USED: 'blue',
  EXPIRED: 'gray',
  REVOKED: 'red',
};

/** Срок действия по М-2 — обычно 15 дней с даты выдачи. */
export const DEFAULT_VALIDITY_DAYS = 15;

export interface PowerOfAttorneyLine {
  id: string;
  lineNumber: number;
  nomenclatureId: string | null;
  name: string;
  unitId: string | null;
  unitSymbol: string | null;
  quantity: string;
}

export interface PowerOfAttorneyLineInput {
  nomenclatureId?: string | null;
  name: string;
  unitId?: string | null;
  quantity: string;
}

export interface PoaOrganizationRef {
  id: string;
  shortName: string;
  fullName: string;
  kind: OrganizationKind;
  inn: string;
  kpp: string | null;
  okpo: string | null;
  addressFull: string | null;
  directorPosition: string | null;
  directorName: string | null;
  accountantName: string | null;
  accountantIsDirector: boolean;
  /** Основной счёт идёт первым — для граф «Счет №» и «наименование банка» бланка М-2 */
  bankAccounts: OrganizationBankAccount[];
}

export interface PoaCounterpartyRef {
  id: string;
  shortName: string;
  fullName: string;
  inn: string | null;
  kpp: string | null;
  addressFull: string | null;
}

export interface PowerOfAttorney {
  id: string;
  organizationId: string;
  organization: PoaOrganizationRef;
  number: string;
  numberInt: number;
  date: string;
  validUntil: string;
  employeeId: string;
  employee: Employee;
  employeePosition: string | null;
  employeeName: string;
  counterpartyId: string;
  counterparty: PoaCounterpartyRef;
  supplierName: string;
  basisDocName: string | null;
  basisDocNumber: string | null;
  basisDocDate: string | null;
  status: PowerOfAttorneyStatus;
  executedDocument: string | null;
  executedAt: string | null;
  comment: string | null;
  lines: PowerOfAttorneyLine[];
}

export interface PowerOfAttorneyInput {
  organizationId: string;
  date: string;
  validUntil: string;
  employeeId: string;
  counterpartyId: string;
  basisDocName?: string | null;
  basisDocNumber?: string | null;
  basisDocDate?: string | null;
  comment?: string | null;
  lines: PowerOfAttorneyLineInput[];
}

export interface PowerOfAttorneyListItem {
  id: string;
  organizationId: string;
  organization: { id: string; shortName: string };
  counterpartyId: string;
  counterparty: { id: string; shortName: string };
  employee: { id: string; fullName: string };
  number: string;
  date: string;
  validUntil: string;
  employeeName: string;
  supplierName: string;
  status: PowerOfAttorneyStatus;
}

export interface PowerOfAttorneyListResponse {
  items: PowerOfAttorneyListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PowerOfAttorneyListParams {
  search?: string;
  organizationId?: string;
  counterpartyId?: string;
  employeeId?: string;
  status?: PowerOfAttorneyStatus;
  page?: number;
  pageSize?: number;
}

function buildQuery(params: PowerOfAttorneyListParams): string {
  const search = new URLSearchParams();
  if (params.search) search.set('search', params.search);
  if (params.organizationId) search.set('organizationId', params.organizationId);
  if (params.counterpartyId) search.set('counterpartyId', params.counterpartyId);
  if (params.employeeId) search.set('employeeId', params.employeeId);
  if (params.status) search.set('status', params.status);
  if (params.page !== undefined) search.set('page', String(params.page));
  if (params.pageSize !== undefined) search.set('pageSize', String(params.pageSize));
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const powersOfAttorneyApi = {
  list: (params: PowerOfAttorneyListParams = {}) =>
    api.get<PowerOfAttorneyListResponse>(`/powers-of-attorney${buildQuery(params)}`),

  get: (id: string) => api.get<{ powerOfAttorney: PowerOfAttorney }>(`/powers-of-attorney/${id}`),

  create: (data: PowerOfAttorneyInput) =>
    api.post<{ powerOfAttorney: PowerOfAttorney }>('/powers-of-attorney', data),

  createFromInvoice: (invoiceId: string, data: { employeeId: string; date?: string; validUntil?: string }) =>
    api.post<{ powerOfAttorney: PowerOfAttorney }>(`/powers-of-attorney/from-invoice/${invoiceId}`, data),

  update: (id: string, data: PowerOfAttorneyInput) =>
    api.patch<{ powerOfAttorney: PowerOfAttorney }>(`/powers-of-attorney/${id}`, data),

  execute: (id: string, executedDocument: string) =>
    api.post<{ powerOfAttorney: PowerOfAttorney }>(`/powers-of-attorney/${id}/execute`, { executedDocument }),

  revoke: (id: string) => api.post<{ powerOfAttorney: PowerOfAttorney }>(`/powers-of-attorney/${id}/revoke`),

  remove: (id: string) => api.delete<void>(`/powers-of-attorney/${id}`),
};
